import { Dialog, Transition } from "@headlessui/react";
import { PlusIcon, XIcon } from "@heroicons/react/outline";
import { PencilIcon, TrashIcon } from "@heroicons/react/solid";
import { Field, FormikHelpers, FormikProps } from "formik";
import produce from "immer";
import { every, find, isEmpty, toLower, values, without, words } from "lodash";
import moment from "moment";
import { stringifyUrl } from "query-string";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import * as Yup from "yup";
import { useInstalls } from ".";
import DateInput from "../../components/forms/DateInput";
import DropdownSearch from "../../components/forms/DropdownSearch";
import FormProvider from "../../components/forms/FormProvider";
import { getLabelColorStyle } from "../../components/labels/utils";
import { getCommissionPeriodText } from "../../pages/Map/ContactDetails/ContactCommissionsTab/ContactCommissionsTable";
import classNames from "../../utils/classNames";
import { avatarsApi } from "../../utils/constants";
import { formatDateToLocale, locale } from "../../utils/dates";
import { formatHotspotName } from "../../utils/hotspotUtils";
import { h3ToCenterChildMemoized } from "../../utils/memoizedMethods";
import {
  CommissionTemplate,
  CommissionType,
  Hotspot,
  HotspotLocation,
  Install,
  InstallCommission,
  Member
} from "../../utils/types";
import { CurrencyKey } from "../../utils/useCurrency";
import useMapCenter from "../../utils/useMapCenter";
import usePrevious from "../../utils/usePrevious";
import { useAppearance } from "../appearance";
import { useCommissionTemplates } from "../commissionTemplates";
import { useContacts } from "../contacts";
import { useHotspots } from "../hotspots";
import {
  convertEtlHotspotSearchResponse,
  HotspotsSearchResponse
} from "../hotspots/HotspotsProvider";
import { useLocations } from "../locations";
import {
  isHotspotLocation,
  LocationType
} from "../locations/LocationFormSidebar";
import { useMembers } from "../members";
import { getMemberAvatarUrl } from "../members/utils";
import { useToast } from "../toast";
import { useWallets } from "../wallets";
import InstallCommissionFormSidebar, {
  EditInstallCommissionConfig
} from "./InstallCommissionFormSidebar";

interface Props {
  install?: Install | undefined;
  open: boolean;
  toggleEditInstallForm: (install: Install | undefined) => void;
  onSuccess?: (install: Install) => void;
}

const InstallFormSidebar: React.FC<Props> = ({
  install,
  open,
  toggleEditInstallForm,
  onSuccess
}) => {
  const { setModalWarningConfig, modalWarningConfig } = useToast();
  const { installs, addInstall, updateInstall, removeCommission } =
    useInstalls();
  const {
    commissionTemplates,
    getCommissionValueLabel,
    getCommissionTemplateLabel
  } = useCommissionTemplates();
  const { workspaceHotspots, addWorkspaceHotspot } = useHotspots();
  const {
    locations,
    setEditLocationConfig,
    editLocationConfig,
    findLocationIdByCoordinates
  } = useLocations();
  const { contacts } = useContacts();
  const { members } = useMembers();
  const { addWallet } = useWallets();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const centerInH3HexRes4 = useMapCenter((state) => state.centerInH3HexRes4);
  const [activeTab, setActiveTab] = useState<"wallet" | "hotspot">("wallet");
  const dialogRef = useRef(null);
  const formRef = useRef<FormikProps<any>>(null);
  const now = useRef(new Date());
  const [commissions, setCommissions] = useState<InstallCommission[]>([]);
  const [editInstallCommissionConfig, setEditInstallCommissionConfig] =
    useState<EditInstallCommissionConfig>({ show: false });
  const { isDark } = useAppearance();
  const [showAlertModal, setShowAlertModal] = useState(false);

  const handleCommissions = useCallback(
    (
      commission: InstallCommission,
      action: "add" | "edit",
      commissionIndex: number
    ) =>
      setCommissions((prevState) =>
        produce(prevState, (draft) => {
          switch (action) {
            case "add":
              draft.push(commission);
              break;
            case "edit":
              draft[commissionIndex] = commission;
          }
        })
      ),
    []
  );

  const addCommissionsForLocationContacts = useCallback(
    (contactIds: string[]) => {
      if (isEmpty(commissionTemplates)) return;

      const defaultCommissionTemplate = find(
        values(commissionTemplates),
        "primary"
      );

      if (!defaultCommissionTemplate) return;

      contactIds.forEach((contactId) => {
        if (commissions.filter((c) => c.uuid === contactId).length) return;

        const duplicatedInstallCommission = commissions.find(
          (commission) =>
            commission.currency ===
              CurrencyKey[defaultCommissionTemplate.currency] &&
            commission.type === defaultCommissionTemplate.type &&
            commission.contact_uuid === contactId &&
            commission.value === defaultCommissionTemplate.value
        );

        if (duplicatedInstallCommission) {
          return;
        }

        handleCommissions(
          {
            contact_uuid: contactId,
            type: defaultCommissionTemplate.type,
            value: defaultCommissionTemplate.value,
            currency: CurrencyKey[defaultCommissionTemplate.currency],
            start_date: moment(now.current).format("YYYY-MM-DD"),
            end_date: null,
            template_uuid: defaultCommissionTemplate.uuid,
            uuid: null,
            cron_expression: null
          },
          "add",
          0
        );
      });
    },
    [commissionTemplates, commissions, handleCommissions]
  );

  const prevInstall = usePrevious(install);
  useEffect(() => {
    if (install && install !== prevInstall) {
      setCommissions(install?.commissions || []);

      const linkedContactIds =
        locations[install.location_uuid]?.linked_contact_ids;

      if (linkedContactIds && !install.uuid) {
        addCommissionsForLocationContacts(linkedContactIds);
      }
    }
  }, [addCommissionsForLocationContacts, install, locations, prevInstall]);

  const closeSidebar = useCallback(
    (install?: Install) => {
      toggleEditInstallForm(install);
      setEditLocationConfig({ show: false });
      setEditInstallCommissionConfig({ show: false });
      setCommissions([]);
      setErrorMessage(null);
    },
    [toggleEditInstallForm, setEditLocationConfig]
  );

  const initialValues = useMemo(
    () =>
      install
        ? {
            hotspot: find(workspaceHotspots, {
              id: install.hotspot_uuid
            }),
            location: locations[install.location_uuid],
            installed_at: install.installed_at
              ? moment(install.installed_at).toDate()
              : now.current,
            uninstalled_at: install.uninstalled_at
              ? moment(install.uninstalled_at).toDate()
              : undefined
          }
        : {
            hotspot: undefined,
            location: undefined,
            installed_at: now.current,
            uninstalled_at: undefined
          },
    [install, locations, workspaceHotspots]
  );

  const checkCommissionStartDateMatch = useCallback(
    (formValues: FormValues) => {
      let valid = true;

      commissions.forEach((item) => {
        if (
          moment(item.start_date).format("YYYY-MM-DD") !==
          moment(formValues.installed_at).format("YYYY-MM-DD")
        ) {
          valid = false;
        }
      });

      return valid;
    },
    [commissions]
  );

  const onSubmit = useCallback(
    async (
      formValues: FormValues,
      formikHelpers: FormikHelpers<FormValues>,
      withComissionValidateDate?: boolean
    ) => {
      setErrorMessage(null);

      if (
        !checkCommissionStartDateMatch(formValues) &&
        withComissionValidateDate
      ) {
        setModalWarningConfig({
          title: "Confirm commission dates",
          confirmButtonText: "Confirm",
          confirmButtonClassname:
            "w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-hotspotty-600 text-base font-medium text-white hover:bg-hotspotty-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-500 md:ml-3 sm:w-auto sm:text-sm",
          message:
            "One of your commissions has a different start date than the date at which the hotspot was installed. Are you sure you want to proceed?",
          open: true,
          resolve: () => {
            formikHelpers.setSubmitting(true);
            onSubmit(formValues, formikHelpers, false);
          }
        });

        return;
      }

      const hotspotIsAlreadyTracked = !!find(values(workspaceHotspots), {
        id: formValues.hotspot.id
      });

      if (!hotspotIsAlreadyTracked) {
        switch (activeTab) {
          case "wallet":
            await addWallet(formValues.hotspot.owner);
            break;
          case "hotspot":
            await addWorkspaceHotspot(formValues.hotspot.id);
            break;
        }
      }

      const installToSave = {
        hotspot_uuid: formValues.hotspot.id,
        location_uuid: formValues.location.uuid,
        installed_at: getFormattedDate(formValues.installed_at) || null,
        uninstalled_at: getFormattedDate(formValues.uninstalled_at) || null,
        commissions
      };

      let apiResponse;

      if (install?.uuid) {
        apiResponse = await updateInstall(install.uuid, installToSave);
      } else {
        apiResponse = await addInstall(installToSave);
      }

      if (apiResponse?.status) {
        closeSidebar(apiResponse.data);
        onSuccess && onSuccess(apiResponse.data);
      } else {
        setErrorMessage("Something went wrong");
      }

      formikHelpers.setSubmitting(false);
    },
    [
      checkCommissionStartDateMatch,
      workspaceHotspots,
      commissions,
      install?.uuid,
      setModalWarningConfig,
      activeTab,
      addWallet,
      addWorkspaceHotspot,
      updateInstall,
      addInstall,
      closeSidebar,
      onSuccess
    ]
  );

  const fetchHotspots = useCallback(
    async (searchInput: string | undefined) => {
      const findHotspotsUrl = stringifyUrl({
        url: `${process.env.REACT_APP_ETL_API}/hotspots/search-lean`,
        query: {
          name: searchInput || "",
          limit: 5,
          ...(centerInH3HexRes4
            ? {
                proximity_hex: h3ToCenterChildMemoized(centerInH3HexRes4, 12)
              }
            : {})
        }
      });

      return fetch(findHotspotsUrl)
        .then((res) => res.json())
        .then(
          (res: HotspotsSearchResponse) =>
            convertEtlHotspotSearchResponse(res.data) || []
        );
    },
    [centerInH3HexRes4]
  );

  const validateHotspotField = useCallback(
    (hotspot: Hotspot | undefined, formValues: FormValues) => {
      if (!hotspot) return "Required";

      if (hotspot.id === install?.hotspot_uuid && install.location_uuid) {
        return;
      }

      if (!formValues.installed_at) return;

      const formValuesInstalledAtMoment = moment(formValues.installed_at)
        .utc()
        .startOf("date");

      let hasOverlap = false;

      const installsForHotspot = values(installs).filter(
        (install) =>
          install.hotspot_uuid === hotspot.id && !!install.installed_at
      );

      installsForHotspot.forEach((otherInstall) => {
        if (hasOverlap) return;

        const otherInstallInstalledAtMoment = moment(otherInstall.installed_at)
          .utc()
          .startOf("date");

        if (formValues.uninstalled_at) {
          const formValuesUninstalledAtMoment = moment(
            formValues.uninstalled_at
          )
            .utc()
            .startOf("date");

          if (otherInstall.uninstalled_at) {
            // The form install is a fixed period
            // The other install is a fixed period
            // Implementation from https://stackoverflow.com/a/44800607

            let intallPeriods = [
              [
                formValuesInstalledAtMoment.valueOf(),
                formValuesUninstalledAtMoment.valueOf()
              ],
              [
                otherInstallInstalledAtMoment.valueOf(),
                moment(otherInstall.uninstalled_at).utc().valueOf()
              ]
            ];

            intallPeriods.sort((periodA, periodB) => periodA[0] - periodB[0]);

            for (let i = 0; i < intallPeriods.length - 1; i++) {
              if (hasOverlap) return;

              const currentEndTime = intallPeriods[i][1];
              const nextStartTime = intallPeriods[i + 1][0];

              if (currentEndTime >= nextStartTime) hasOverlap = true;
            }
          } else {
            // The form install is a fixed period
            // The other install has no end date
            if (
              otherInstallInstalledAtMoment.isSameOrBefore(
                formValuesInstalledAtMoment
              ) ||
              otherInstallInstalledAtMoment.isSameOrBefore(
                formValuesUninstalledAtMoment
              )
            ) {
              hasOverlap = true;
            }
          }
        } else {
          if (otherInstall.uninstalled_at) {
            // The form install has no end date
            // The other install is a fixed period

            if (
              formValuesInstalledAtMoment.isSameOrBefore(
                otherInstallInstalledAtMoment
              ) ||
              formValuesInstalledAtMoment.isSameOrBefore(
                otherInstall.uninstalled_at
              )
            ) {
              hasOverlap = true;
            }
          } else {
            // The form install has no end date
            // The other install has no end date
            hasOverlap = true;
          }
        }
      });

      if (hasOverlap) {
        return "This hotspot has other installs linked, please ensure that there is no overlap with their installation periods";
      }
    },
    [install?.hotspot_uuid, install?.location_uuid, installs]
  );

  const getLocations = useCallback(
    (searchInput: string | undefined) => {
      const filteredLocations = values(locations).filter((location) => {
        if (searchInput) {
          const searchInputWords = words(searchInput, /[^, ]+/g).map(toLower);

          return every(
            searchInputWords,
            (word) =>
              toLower(location.name).includes(word) ||
              toLower(location.address || "").includes(word)
          );
        }
        return true;
      });

      return filteredLocations.slice(0, 10);
    },
    [locations]
  );

  const getCommissionTemplates = useCallback(
    (searchInput: string | undefined) => {
      const filteredCommissionTemplates = values(commissionTemplates).filter(
        (commissionTemplate) => {
          if (searchInput) {
            const searchInputWords = words(searchInput, /[^, ]+/g).map(toLower);

            return every(searchInputWords, (word) =>
              toLower(commissionTemplate.name).includes(word)
            );
          }
          return true;
        }
      );

      return filteredCommissionTemplates.slice(0, 10);
    },
    [commissionTemplates]
  );

  const validateLocationField = useCallback(
    (location: HotspotLocation | undefined) => {
      if (!location) {
        return "Required";
      }
    },
    []
  );

  const renderAddHotspotOrWallet = useCallback(
    (formValues: FormValues) => {
      if (!formValues.hotspot) return null;

      const hotspotIsAlreadyTracked = !!find(values(workspaceHotspots), {
        id: formValues.hotspot.id
      });

      if (!hotspotIsAlreadyTracked) {
        return (
          <div>
            <p className="mb-2 text-sm text-gray-600 dark:text-white">
              This hotspot will be added to your workspace:
            </p>
            <div className="group p-0.5 rounded-lg flex bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700">
              <button
                type="button"
                className="flex-1 flex focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 rounded-md focus:outline-none focus-visible:ring-offset-gray-100"
                onClick={() => setActiveTab("wallet")}
              >
                <span
                  className={classNames(
                    "flex-1 p-1.5 lg:pl-2.5 lg:pr-3.5 rounded-md text-sm focus:outline-none font-medium text-center",
                    activeTab === "wallet"
                      ? "bg-white shadow-sm ring-1 ring-black ring-opacity-5 dark:bg-gray-500"
                      : "text-gray-600 focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-100"
                  )}
                >
                  <span
                    className={classNames(
                      activeTab === "wallet"
                        ? "text-gray-900 dark:text-gray-100"
                        : "text-gray-600 group-hover:text-gray-900 dark:text-gray-300 dark:group-hover:text-gray-100"
                    )}
                  >
                    Add hotspot's wallet
                  </span>
                </span>
              </button>
              <button
                type="button"
                className={classNames(
                  "flex-1 p-1.5 lg:pl-2.5 lg:pr-3.5 rounded-md text-sm focus:outline-none font-medium text-center",
                  activeTab === "hotspot"
                    ? "bg-white shadow-sm ring-1 ring-black ring-opacity-5 dark:bg-gray-500"
                    : "text-gray-600 focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-100"
                )}
                onClick={() => setActiveTab("hotspot")}
              >
                <span
                  className={classNames(
                    "text-center",
                    activeTab === "hotspot"
                      ? "text-gray-900 dark:text-gray-100"
                      : "text-gray-600 group-hover:text-gray-900 dark:text-gray-300 dark:group-hover:text-gray-100"
                  )}
                >
                  Just add this hotspot
                </span>
              </button>
            </div>
          </div>
        );
      }

      return null;
    },
    [activeTab, workspaceHotspots]
  );

  const checkCanShow = useCallback(() => {
    if (editLocationConfig?.show || showAlertModal || modalWarningConfig.open)
      return false;

    return open;
  }, [editLocationConfig?.show, showAlertModal, modalWarningConfig.open, open]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-20 overflow-hidden"
        open={checkCanShow()}
        onClose={() => {
          if (
            editLocationConfig?.show ||
            editInstallCommissionConfig?.parentForm === "install" ||
            showAlertModal ||
            modalWarningConfig.open
          )
            return;

          closeSidebar();
        }}
        initialFocus={dialogRef}
      >
        <InstallCommissionFormSidebar
          config={editInstallCommissionConfig}
          setConfig={setEditInstallCommissionConfig}
        />
        <div ref={dialogRef} className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div
            className={classNames(
              "fixed inset-y-0 pl-16 max-w-full right-0 flex duration-500",
              (editLocationConfig?.show ||
                editInstallCommissionConfig?.parentForm === "install") &&
                "pr-40"
            )}
          >
            <div className="absolute inset-0" onClick={() => closeSidebar()} />
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="relative w-screen max-w-md">
                <FormProvider
                  classnames={{ wrapper: "h-full" }}
                  onCancel={closeSidebar}
                  error={errorMessage}
                  formikProps={{
                    onSubmit: (values, formikHelpers) =>
                      onSubmit(values, formikHelpers, true),
                    initialValues,
                    validationSchema,
                    innerRef: formRef
                  }}
                  orderedFieldNames={ORDERED_FIELD_NAMES}
                >
                  {({
                    isSubmitting,
                    values,
                    setFieldValue
                  }: FormikProps<FormValues>) => {
                    return (
                      <div className="flex-1 h-0 overflow-y-auto">
                        <div className="py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                          <div className="flex items-center justify-between">
                            <Dialog.Title className="text-lg font-medium text-white">
                              {install?.uuid ? "Edit" : "New"} install
                            </Dialog.Title>
                            <div className="ml-3 h-7 flex items-center">
                              <button
                                autoFocus
                                type="button"
                                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                                onClick={() => closeSidebar()}
                              >
                                <span className="sr-only">Close panel</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex-1 flex flex-col justify-between">
                          <div className="px-4 divide-y divide-gray-200 dark:divide-gray-500 sm:px-6">
                            <div className="space-y-6 pt-6 pb-5">
                              <Field
                                label={
                                  <div className="flex items-center justify-between">
                                    <div>Hotspot</div>
                                    {!install?.uuid &&
                                      values.hotspot &&
                                      !!values.hotspot.lat &&
                                      !!values.hotspot.lng && (
                                        <div
                                          onClick={() => {
                                            const hotspotLocation =
                                              findLocationIdByCoordinates(
                                                values.hotspot.lat,
                                                values.hotspot.lng
                                              );

                                            if (hotspotLocation) {
                                              setFieldValue(
                                                "location",
                                                hotspotLocation
                                              );

                                              return;
                                            }

                                            setEditLocationConfig({
                                              show: true,
                                              location: {
                                                address: null,
                                                created_at: "",
                                                created_by: "",
                                                h3_index_r12: "",
                                                h3_index_r4: "",
                                                height: 0,
                                                lat: values.hotspot.lat,
                                                linked_contact_ids: [],
                                                lng: values.hotspot.lng,
                                                metadata: null,
                                                name: `${formatHotspotName(
                                                  values.hotspot
                                                )}'s location`,
                                                updated_at: "",
                                                updated_by: "",
                                                uuid: "",
                                                super_simulations: [],
                                                comments: []
                                              },
                                              onSuccess: (
                                                location: LocationType
                                              ) => {
                                                isHotspotLocation(location) &&
                                                  setFieldValue(
                                                    "location",
                                                    location
                                                  );

                                                if (
                                                  location &&
                                                  isHotspotLocation(location) &&
                                                  !isEmpty(
                                                    location.linked_contact_ids
                                                  )
                                                ) {
                                                  addCommissionsForLocationContacts(
                                                    location.linked_contact_ids
                                                  );
                                                }
                                              }
                                            });
                                          }}
                                          className="flex items-center text-sm text-gray-500 dark:text-white cursor-pointer"
                                        >
                                          <PlusIcon className="h-4 w-4 ml-1 text-gray-500 dark:text-white" />
                                          Use hotspot location
                                        </div>
                                      )}
                                  </div>
                                }
                                name="hotspot"
                                placeholder="Search a hotspot"
                                disabled={isSubmitting || install?.uuid}
                                getImageUrl={(hotspot: Hotspot) =>
                                  stringifyUrl({
                                    url: `${avatarsApi}/bottts/${hotspot.id}.svg`,
                                    query: { w: 24, h: 24 }
                                  })
                                }
                                fetchItems={fetchHotspots}
                                itemToString={formatHotspotName}
                                component={DropdownSearch}
                                validate={(hotspot: Hotspot | undefined) =>
                                  validateHotspotField(hotspot, values)
                                }
                              />

                              {renderAddHotspotOrWallet(values)}

                              <Field
                                label={
                                  <div className="flex items-center justify-between">
                                    <div>Location</div>
                                    {!install?.uuid && (
                                      <div
                                        onClick={() => {
                                          setEditLocationConfig({
                                            show: true,
                                            onSuccess: (
                                              location: LocationType
                                            ) => {
                                              isHotspotLocation(location) &&
                                                setFieldValue(
                                                  "location",
                                                  location
                                                );

                                              if (
                                                location &&
                                                isHotspotLocation(location) &&
                                                !isEmpty(
                                                  location.linked_contact_ids
                                                )
                                              ) {
                                                addCommissionsForLocationContacts(
                                                  location.linked_contact_ids
                                                );
                                              }
                                            }
                                          });
                                        }}
                                        className="flex items-center text-sm text-gray-500 dark:text-white cursor-pointer"
                                      >
                                        <PlusIcon className="h-4 w-4 ml-1 text-gray-500 dark:text-white" />
                                        Add new location
                                      </div>
                                    )}
                                  </div>
                                }
                                name="location"
                                placeholder={
                                  isEmpty(locations)
                                    ? "No locations available"
                                    : "Search locations"
                                }
                                disabled={
                                  isSubmitting ||
                                  isEmpty(locations) ||
                                  install?.uuid
                                }
                                fetchItems={getLocations}
                                itemToString={(location: HotspotLocation) =>
                                  location.name
                                }
                                component={DropdownSearch}
                                validate={validateLocationField}
                                withoutMinimumSearchCharacters
                                initialItems={getLocations("")}
                                onSelectedItem={(
                                  location: HotspotLocation | undefined
                                ) => {
                                  if (
                                    location &&
                                    !isEmpty(location.linked_contact_ids)
                                  ) {
                                    addCommissionsForLocationContacts(
                                      location.linked_contact_ids
                                    );
                                  }
                                }}
                              />

                              <Field
                                id="installed_at"
                                name="installed_at"
                                label="Installed"
                                placeholder={`E.g. ${new Date().toLocaleDateString(
                                  locale
                                )}`}
                                max={
                                  values.uninstalled_at &&
                                  moment(values.uninstalled_at)
                                    .utc()
                                    .isBefore(moment(now.current).utc())
                                    ? values.uninstalled_at
                                    : now.current
                                }
                                component={DateInput}
                                disabled={isSubmitting}
                              />

                              <Field
                                id="uninstalled_at"
                                name="uninstalled_at"
                                label="Uninstalled"
                                placeholder={`E.g. ${new Date().toLocaleDateString(
                                  locale
                                )}`}
                                min={moment(values.installed_at)
                                  .utc()
                                  .add(1, "days")
                                  .toDate()}
                                max={now.current}
                                component={DateInput}
                                disabled={
                                  isSubmitting ||
                                  moment(values.installed_at)
                                    .utc()
                                    .add(1, "days")
                                    .isAfter(moment(now.current).utc())
                                }
                              />

                              <Field
                                label={
                                  <div className="flex items-center justify-between">
                                    <div>Commissions</div>
                                    <div
                                      onClick={() => {
                                        setEditInstallCommissionConfig({
                                          show: true,
                                          installId: install?.uuid,
                                          installCommissions: commissions,
                                          parentForm: "install",
                                          commission: {
                                            contact_uuid: "",
                                            type: CommissionType.percentage,
                                            value: 0,
                                            template_uuid: "",
                                            currency: CurrencyKey.hnt,
                                            start_date:
                                              values.installed_at ||
                                              now.current.toISOString(),
                                            end_date: undefined
                                          },
                                          onSuccess: (
                                            commission: InstallCommission
                                          ) => {
                                            handleCommissions(
                                              commission,
                                              "add",
                                              0
                                            );
                                          }
                                        });
                                      }}
                                      className="flex items-center text-sm text-gray-500 dark:text-white cursor-pointer"
                                    >
                                      <PlusIcon className="h-4 w-4 ml-1 text-gray-500 dark:text-white" />
                                      Add new commission
                                    </div>
                                  </div>
                                }
                                name="selectedCommissionTemplate"
                                placeholder={
                                  isEmpty(commissionTemplates)
                                    ? "No commission templates available"
                                    : "Select commission template"
                                }
                                disabled={
                                  isSubmitting || isEmpty(commissionTemplates)
                                }
                                fetchItems={getCommissionTemplates}
                                itemToString={(
                                  commissionTemplate: CommissionTemplate
                                ) => commissionTemplate.name}
                                component={DropdownSearch}
                                withoutMinimumSearchCharacters
                                onSelectedItem={(
                                  commissionTemplate:
                                    | CommissionTemplate
                                    | undefined,
                                  clearInputCallback: () => void
                                ) => {
                                  if (!commissionTemplate) return;

                                  setEditInstallCommissionConfig({
                                    show: true,
                                    installId: install?.uuid,
                                    installCommissions: commissions,
                                    parentForm: "install",
                                    commission: {
                                      contact_uuid: "",
                                      type: commissionTemplate.type,
                                      value: Number(commissionTemplate.value),
                                      template_uuid: commissionTemplate.uuid,
                                      currency:
                                        CurrencyKey[
                                          commissionTemplate.currency
                                        ],
                                      start_date:
                                        values.installed_at ||
                                        now.current.toISOString(),
                                      end_date: undefined
                                    },
                                    onSuccess: (
                                      commission: InstallCommission
                                    ) => {
                                      handleCommissions(commission, "add", 0);
                                    }
                                  });

                                  clearInputCallback();
                                }}
                                initialItems={getCommissionTemplates("")}
                              />

                              {!isEmpty(commissions) && (
                                <div className="flex flex-col w-full shadow overflow-hidden sm:rounded-lg">
                                  <div className="bg-white divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800">
                                    {commissions.map(
                                      (commission, commissionIndex) => {
                                        const contact =
                                          contacts[commission.contact_uuid];
                                        const member = find(members, {
                                          email: contact?.email
                                        }) as Member | undefined;

                                        return (
                                          <div
                                            key={commissionIndex}
                                            className="px-6 py-4 whitespace-nowraprelative flex items-center space-x-3 group"
                                          >
                                            <div className="flex-shrink-0">
                                              <img
                                                className="h-10 w-10 rounded-full"
                                                src={
                                                  contact?.email && member
                                                    ? getMemberAvatarUrl(
                                                        member,
                                                        40
                                                      )
                                                    : stringifyUrl({
                                                        url: `${avatarsApi}/human/${contact?.uuid}.svg`,
                                                        query: {
                                                          w: 40,
                                                          h: 40
                                                        }
                                                      })
                                                }
                                                onError={(e: any) => {
                                                  e.target.onerror = null;
                                                  e.target.src = stringifyUrl({
                                                    url: `${avatarsApi}/human/${contact?.uuid}.svg`,
                                                    query: {
                                                      w: 40,
                                                      h: 40
                                                    }
                                                  });
                                                }}
                                                alt="Contact avatar"
                                              />
                                            </div>
                                            <div className="flex-1 min-w-0 focus:outline-none">
                                              <div className="flex items-center">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate items-center">
                                                  {contact?.name ||
                                                    "Removed contact"}{" "}
                                                  {member &&
                                                    `(${member?.name})`}
                                                </p>
                                              </div>

                                              <p className="text-xs font-light text-gray-500 truncate dark:text-gray-400">
                                                {getCommissionPeriodText(
                                                  commission
                                                )}
                                              </p>

                                              <div className="flex items-center flex-wrap">
                                                <div
                                                  className="inline-flex items-center rounded-full font-medium px-3 text-sm mr-2 mt-2"
                                                  style={getLabelColorStyle(
                                                    "#7057ff",
                                                    isDark
                                                  )}
                                                >
                                                  {getCommissionTemplateLabel(
                                                    commission
                                                  )}
                                                </div>
                                                <div
                                                  className="inline-flex items-center rounded-full font-medium px-3 text-sm mt-2"
                                                  style={getLabelColorStyle(
                                                    "#d73aaa",
                                                    isDark
                                                  )}
                                                >
                                                  {getCommissionValueLabel(
                                                    commission
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                            <button
                                              className="inline-flex rounded-md p-1.5 focus:outline-none text-hotspotty-400 hover:bg-hotspotty-100 dark:text-white dark:hover:text-gray-800 disabled:opacity-50 disabled:cursor-default"
                                              type="button"
                                              disabled={isSubmitting}
                                              onClick={() => {
                                                setEditInstallCommissionConfig({
                                                  show: true,
                                                  installId: install?.uuid,
                                                  installCommissions:
                                                    commissions,
                                                  parentForm: "install",
                                                  commissionIndex:
                                                    commissionIndex,
                                                  commission,
                                                  onSuccess: (
                                                    item: InstallCommission
                                                  ) => {
                                                    handleCommissions(
                                                      item,
                                                      "edit",
                                                      commissionIndex
                                                    );
                                                  }
                                                });
                                              }}
                                            >
                                              <PencilIcon
                                                className="h-5 w-5"
                                                aria-hidden="true"
                                              />
                                            </button>
                                            <button
                                              style={{ marginLeft: 0 }}
                                              className="inline-flex rounded-md p-1.5 focus:outline-none text-hotspotty-400 hover:bg-hotspotty-100 dark:text-white dark:hover:text-gray-800 disabled:opacity-50 disabled:cursor-default"
                                              type="button"
                                              disabled={isSubmitting}
                                              onClick={() => {
                                                setShowAlertModal(true);
                                                setModalWarningConfig({
                                                  open: true,
                                                  cancel: () => {
                                                    setShowAlertModal(false);
                                                  },
                                                  resolve: () => {
                                                    setCommissions(
                                                      (prevState) =>
                                                        without(
                                                          prevState,
                                                          commission
                                                        )
                                                    );

                                                    if (
                                                      install?.uuid &&
                                                      commission.uuid
                                                    ) {
                                                      removeCommission(
                                                        install?.uuid,
                                                        commission.uuid
                                                      );
                                                    }
                                                  }
                                                });
                                              }}
                                            >
                                              <TrashIcon
                                                className="h-5 w-5"
                                                aria-hidden="true"
                                              />
                                            </button>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                </FormProvider>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default InstallFormSidebar;

//
// Utils
//

interface FormValues {
  hotspot: Hotspot;
  location: HotspotLocation;
  installed_at: string;
  uninstalled_at: string | null;
}

const ORDERED_FIELD_NAMES = [
  "hotspot",
  "location",
  "installed_at",
  "uninstalled_at"
];

export const getFormattedDate = (date?: string | null) => {
  if (!date) return "";

  return moment(date).local().format("YYYY-MM-DD");
};

const validationSchema = Yup.object().shape(
  {
    installed_at: Yup.date().when(
      "uninstalled_at",
      (date: Date | undefined, schema: Record<any, any>) => {
        const validDate = moment(date);
        const currentDate = moment(new Date());

        if (!date || !validDate.isValid()) {
          return schema.max(currentDate, "Must be before or equal today");
        }

        if (validDate.isBefore(currentDate)) {
          return schema.max(
            validDate,
            `Must be before ${formatDateToLocale(validDate.toLocaleString())}`
          );
        }

        return schema.max(currentDate, "Must be before or equal today");
      }
    ),
    uninstalled_at: Yup.date()
      .nullable()
      .when(
        "installed_at",
        (date: Date | undefined, schema: Record<any, any>) => {
          const validDate = moment(date).add(1, "days");
          const currentDate = moment(new Date());

          if (!date || !validDate.isValid()) {
            return schema.optional();
          }

          return schema
            .min(
              validDate.toDate(),
              `Uninstalled at field must be later than or equal to ${formatDateToLocale(
                moment(date).toLocaleString()
              )}`
            )
            .max(currentDate, "Must be before or equal today");
        }
      )
  },
  [["installed_at", "uninstalled_at"]]
);
